/**
 * Error Handler Utility
 * Provides centralized error handling and user feedback
 */

import { message, notification } from 'antd';
import { history } from '@umijs/max';
import { formatLocaleMessage } from './formatLocaleMessage';

export const isApiRequestCanceled = (error: any): boolean => {
  if (!error) return false;
  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';
  return (
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.__CANCEL__ === true ||
    message === 'canceled' ||
    message === 'cancelled'
  );
};

export const isRequestTimeoutError = (error: any): boolean => {
  if (!error || isApiRequestCanceled(error)) return false;

  if (error.response && Number(error.response.status) !== 0) return false;

  const code = String(error.code || '').toUpperCase();
  return (
    code === 'ETIMEDOUT' ||
    error.name === 'TimeoutError' ||
    (code === 'ECONNABORTED' && /timeout|timed out/i.test(String(error.message || '')))
  );
};

export const isNetworkRequestError = (error: any): boolean => {
  if (!error || isApiRequestCanceled(error) || isRequestTimeoutError(error)) return false;

  const responseStatus = Number(error.response?.status);
  if (error.response && responseStatus !== 0) return false;

  const code = String(error.code || '').toUpperCase();
  const errorMessage = String(error.message || '').toLowerCase();

  return (
    responseStatus === 0 ||
    Boolean(error.request) ||
    code === 'ERR_NETWORK' ||
    code === 'ECONNRESET' ||
    code === 'ECONNREFUSED' ||
    errorMessage === 'network error' ||
    errorMessage.includes('failed to fetch')
  );
};

export interface ApiError {
  status?: number;
  message?: string;
  error?: string;
  details?: any;
}

export interface ErrorHandlerOptions {
  showNotification?: boolean;
  showMessage?: boolean;
  redirect?: boolean;
  customMessage?: string;
}

/**
 * Handle API errors with appropriate user feedback
 */
export const handleApiError = (error: any, options: ErrorHandlerOptions = {}) => {
  const { showNotification = false, showMessage = true, redirect = true, customMessage } = options;

  if (isApiRequestCanceled(error)) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[handleApiError] Request canceled, skipping UI error handling');
    }
    return {
      status: undefined,
      message: 'Request canceled',
      data: null,
    };
  }

  console.error('API Error:', error);

  // Extract error details
  const errorResponse = error?.response;
  const status = errorResponse?.status;
  const errorData = errorResponse?.data;
  const errorMessage =
    errorData?.message || errorData?.error || error?.message || 'Unknown error occurred';

  if (isRequestTimeoutError(error)) {
    const timeoutMessage = customMessage || formatLocaleMessage('requestError.timeout');
    if (showMessage) {
      message.error({ key: 'global-request-timeout', content: timeoutMessage });
    }
    return { status, message: timeoutMessage, data: errorData };
  }

  if (isNetworkRequestError(error)) {
    if (showMessage) {
      message.error({
        key: 'global-network-error',
        content: customMessage || errorMessage,
      });
    }

    return {
      status,
      message: errorMessage,
      data: errorData,
    };
  }

  // Handle different error types
  switch (status) {
    case 401:
      // Unauthorized - clear auth and redirect to login
      if (redirect) {
        localStorage.removeItem('token');
        localStorage.removeItem('user_permissions_cache');
        history.push('/user/login');
      }
      if (showMessage) {
        message.error('Authentication expired. Please login again.');
      }
      break;

    case 403:
      // Forbidden - user lacks permissions
      // Only show error if explicitly requested, to avoid duplicate warnings
      // Permission checks should be handled by the permission hooks/store
      if (showMessage && customMessage) {
        message.error(customMessage);
      } else if (showMessage && !customMessage) {
        // Don't show generic permission errors - they should be handled by permission hooks
        console.debug(
          '[ErrorHandler] 403 error suppressed - should be handled by permission hooks',
        );
      }
      if (showNotification && customMessage) {
        notification.error({
          message: 'Permission Denied',
          description: customMessage,
          duration: 4,
        });
      }
      break;

    case 404:
      // Not found
      if (showMessage) {
        message.error(customMessage || 'Requested resource not found.');
      }
      break;

    case 422:
      // Validation error
      if (showMessage) {
        message.error(customMessage || 'Validation error. Please check your input.');
      }
      if (showNotification && errorData?.errors) {
        const errorList = Object.entries(errorData.errors)
          .map(([field, errors]: [string, any]) => `${field}: ${errors.join(', ')}`)
          .join('\n');

        notification.error({
          message: 'Validation Failed',
          description: errorList,
          duration: 6,
        });
      }
      break;

    case 500:
    case 502:
    case 503:
      // Server error
      // 降噪：對於搜索/提示類非關鍵請求，僅 console，不彈 toast
      if (showMessage) {
        message.error(customMessage || 'Server error. Please try again later.');
      }
      if (showNotification) {
        notification.error({
          message: 'Server Error',
          description: 'An unexpected error occurred. Please contact support if this persists.',
          duration: 5,
        });
      }
      break;

    default:
      // Other errors
      if (showMessage) {
        message.error(customMessage || errorMessage);
      }
  }

  // Log error details for debugging
  console.group('Error Details');
  console.log('Status:', status);
  console.log('Message:', errorMessage);
  console.log('Data:', errorData);
  console.log('Full Error:', error);
  console.groupEnd();

  return {
    status,
    message: errorMessage,
    data: errorData,
  };
};

/**
 * Create a standardized error response
 */
export const createErrorResponse = (message: string, status: number = 500, details?: any) => {
  return {
    success: false,
    error: {
      message,
      status,
      details,
    },
  };
};

/**
 * Check if an error is an authentication error
 */
export const isAuthError = (error: any): boolean => {
  return error?.response?.status === 401;
};

/**
 * Check if an error is a permission error
 */
export const isPermissionError = (error: any): boolean => {
  return error?.response?.status === 403;
};

/**
 * Debug helper for API responses
 */
export const debugApiResponse = (endpoint: string, response: any, error?: any) => {
  const timestamp = new Date().toISOString();

  if (error) {
    if (isApiRequestCanceled(error)) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug(`[debugApiResponse] Request canceled for ${endpoint}`);
      }
      return;
    }
    console.group(`🔴 API Error: ${endpoint} [${timestamp}]`);
    console.error('Endpoint:', endpoint);
    console.error('Error:', error);
    console.error('Response:', error?.response);
    console.error('Status:', error?.response?.status);
    console.error('Data:', error?.response?.data);
    console.groupEnd();
  } else {
    console.group(`🟢 API Success: ${endpoint} [${timestamp}]`);
    console.log('Endpoint:', endpoint);
    console.log('Full Response:', response);
    if (response && typeof response === 'object') {
      console.log('Response Type:', Array.isArray(response) ? 'Array' : 'Object');
      console.log('Response Keys:', Object.keys(response));
      if ('data' in response) {
        console.log('Data:', response.data);
        console.log('Data Type:', Array.isArray(response.data) ? 'Array' : typeof response.data);
        if (Array.isArray(response.data)) {
          console.log('Data Length:', response.data.length);
        }
      }
      if ('success' in response) {
        console.log('Success:', response.success);
      }
      if ('total' in response) {
        console.log('Total:', response.total);
      }
    }
    console.groupEnd();
  }
};
